<template>
  <define-product-count>
    <div aria-live="polite">
      <div v-if="productsLoading" class="h-4 shrink-0 skeleton self-center" style="width: 5.25rem" />
      <p
        v-else
        class="ws-nowrap text-sm c-grey-20 fw-light "
        data-test-id="list-product-count"
      >
        {{ total }} {{ $pluralize($t.products, total) }}
      </p>
    </div>
  </define-product-count>

  <div v-if="sideFilters" class="mb-5 flex items-end gap-2 between <lg:hidden" :class="brandClasses.sideFilters">
    <template v-if="hasFilters">
      <product-count />
      <select-sort
        class="ml-a"
        :label="$t.sort"
        :model-value="sortState"
        :options="sortOptions"
        @update:model-value="$emit('sort', `${$event}`)"
      />
    </template>
    <!-- Skeleton -->
    <template v-else-if="productsLoading">
      <div class="w-full">
        <div class="mt-2 h-4 skeleton" style="width: 5.25rem" />
      </div>
      <div class="h-10 w-40 skeleton" />
    </template>
  </div>
  <base-sticky v-slot="{ isStuck }" name="headerPLP">
    <div
      class="sticky bg-white duration"
      :class="[
        { 'lg:hidden': sideFilters },
        brandClasses.stickyHeader,
        isStuck ? ['z-50 mb-8', brandClasses.stickyHeaderStuck] : 'z-3',
        { [brandClasses.stickyHeaderOnScrollUp]: isStuck && showOnScrollUp && header.pageScrolledUp },
      ]"
      :style="{ top: pxToRem(stickyHeaderTop) }"
    >
      <div class="f-col gap-4 p-4 container">
        <filter-pickup
          v-if="pickupFilters && !user.isWranxOrIpaProfile"
          class="duration"
          :class="{ 'mb-8': !isStuck }"
          :model-value="storeFilterState"
          @update:model-value="$emit('changeStore', $event)"
        />
        <!-- Mobile filters -->
        <div
          v-if="hasFilters || !productsLoading"
          class="flex items-center gap-y-2 wrap lg:hidden"
          :class="hasFilters ? 'between' : 'justify-end'"
        >
          <vf-button
            v-if="hasFilters"
            :class="brandClasses.filterAndSortCta"
            :size="filterAndSortCta.size"
            :variant="filterAndSortCta.variant"
            @click="DialogFiltersParent.open"
          >
            {{ $t.filterAndSort }}
            <vf-icon :class="brandClasses.filterAndSortCtaIcon" name="filters" :size="filterAndSortCta.iconSize" />
          </vf-button>
          <product-count v-if="products.length || pickupFilters" />
        </div>
        <!-- Skeleton -->
        <div v-else class="h-12 w-72 skeleton lg:hidden" />
        <!-- Desktop filters -->
        <div v-if="!sideFilters" class="<lg:hidden">
          <div
            v-if="hasFilters || !productsLoading"
            class="flex items-start gap-3 bg-white"
            :class="hasFilters ? 'between' : 'justify-end'"
          >
            <div v-if="hasFilters" class="flex gap-3 wrap">
              <vf-dropdown
                v-for="(filter, code) in filters"
                :key="code"
                :label="filter.label"
                :style-content="`max-height: 18rem; ${code === 'lowCurrent' ? 'width: 15rem;' : ''}`"
              >
                <filter-options
                  :currency
                  :filter
                  layout="grid"
                  :loading="productsLoading"
                  :model-value="filters[code].selected"
                  @update:model-value="$emit('change', { code: `${code}`, selected: $event! })"
                />
              </vf-dropdown>
            </div>

            <div class="flex items-center gap-4">
              <select-sort
                v-if="products.length"
                :label="$t.sort"
                :model-value="sortState"
                :options="sortOptions"
                @update:model-value="$emit('sort', `${$event}`)"
              />
              <product-count v-if="products.length || pickupFilters" />
            </div>
          </div>
          <!-- Skeleton -->
          <div v-else class="mb-4 flex between">
            <div class="flex gap-4">
              <div v-for="i in 8" :key="i" class="w-20 skeleton" style="height: 2.75rem" />
              <div class="w-40 skeleton" style="height: 2.75rem" />
            </div>
          </div>
        </div>

        <!-- Applied filters section -->
        <filter-display
          v-bind="{ filters, filtersQuery, currency, sideFilters }"
          @clear="$emit('clear')"
          @remove="$emit('remove', $event)"
        />
      </div>
    </div>
  </base-sticky>

  <dialog-filters-parent v-slot="attrs" class-content="f-col py-0" :position="filtersPanelPosition">
    <slot v-bind="attrs" />
  </dialog-filters-parent>
</template>

<script lang="ts" setup>
import type { Catalog, Search } from '#root/api/clients/product/data-contracts'
import type { FiltersMap } from '#types/filters'

export type Props = {
  currency: string
  list: Catalog | Search | null
  productsLoading: boolean
  filters: FiltersMap
  filtersQuery: Record<string, string[]>
  sortOptions: { label: string, value: string }[]
  sortState: string
  storeFilterState?: string
  /**
   * values provided by LaunchDarkly
   */
  sideFilters: boolean
  pickupFilters: boolean
  // overlayTitle has been included specifically for overriding TBL component.
  overlayTitle?: boolean
  // title, overlayTitle has been included specifically for overriding VANS component.
  title?: string
  nextPageLoading?: boolean
}
const props = defineProps<Props>()

defineEmits<Emits>()
export type Emits = {
  clear: []
  remove: [payload: { code: string, id: string }]
  change: [payload: { code: string, selected: string[] }]
  changeStore: [payload?: string]
  sort: [payload: string]
}
const [DefineProductCount, ProductCount] = createReusableTemplate()

const { brandClasses, dialogOptions, filterAndSortCta, filtersPanelPosition } = useAppConfig().components.product.list
const { showOnScrollUp, isStickyPromoBarEnabled } = useAppConfig().components.vf.header
const { $t } = useNuxtApp()
const header = useHeaderStore()
const user = useUserStore()

const stickyHeaderTop = computed(
  () => showOnScrollUp && header.pageScrolledUp
    ? header.height[isStickyPromoBarEnabled ? 'full' : 'header'] - header.height.utility
    : 0
)

const DialogFiltersParent = createDialog('filters', dialogOptions)

const total = computed(() => props.list?.total || 0)
const currency = computed(() => props.list?.currency || 'USD')
const products = computed(() => props.list?.products || [])
const hasFilters = computed(() => !!Object.keys(props.filters)?.length)
</script>
